import { Link, useNavigate } from "@/lib/router-compat";
import { Search, User, LogOut, UserCog, ShieldCheck, ChevronDown, Recycle, Truck, ClipboardList } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAppStore } from "@/store/appStore";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { NotificationBell } from "@/components/NotificationBell";
import logo from "@/assets/logo.webp";

// Defer the live rates ticker — it makes a network call and is not part of
// the marketplace's critical render path.
const ScrapRatesTicker = lazy(() =>
  import("@/components/ScrapRatesTicker").then((m) => ({
    default: m.ScrapRatesTicker,
  })),
);

export const TopNav = () => {
  const session = useAppStore((s) => s.session);
  const profile = useAppStore((s) => s.profile);
  const signOut = useAppStore((s) => s.signOut);
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/buyer?q=${encodeURIComponent(search.trim().slice(0, 80))}`);
  };

  const isSeller = profile?.role === "seller";
  const portalLabel = isSeller ? "Supplier Central" : "Foundry Connect";
  const portalPath = isSeller ? "/seller" : "/buyer";

  return (
    <header className="sticky top-0 z-40 w-full">
      <div className="bg-nav text-nav-foreground">
        <div className="container flex h-14 items-center gap-2 px-3 sm:h-16 sm:gap-3 sm:px-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex flex-none items-center gap-1.5 p-1 transition-opacity duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-nav sm:gap-2.5"
          >
            <img
              src={logo}
              alt="LohaLink logo"
              className="h-10 w-10 object-contain sm:h-12 sm:w-12 md:h-14 md:w-14"
            />
            <div className="hidden flex-col leading-tight sm:flex">
              <span className="text-base font-bold tracking-tight md:text-lg">
                Loha<span className="text-accent">Link</span>
                <span className="ml-0.5 text-[9px] uppercase tracking-widest text-nav-foreground/70 align-top">
                  .in
                </span>
              </span>
              <span className="mt-0.5 text-[9px] italic tracking-wide text-accent/90">
                Metal Linked
              </span>
            </div>
          </Link>

          {/* Explore dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="pop hidden flex-none items-center gap-1 rounded-md px-2 py-1.5 text-sm font-semibold md:inline-flex"
                aria-label="Explore LohaLink"
              >
                Explore <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Explore LohaLink</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/pickup")}>
                <Truck className="mr-2 h-4 w-4 text-accent" /> Book a Scrap Pickup
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/epr")}>
                <Recycle className="mr-2 h-4 w-4 text-accent" /> EPR Credit Brokerage
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => navigate("/rates")}>
                Live Scrap Rates
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/locations")}>
                Scrap Yards by City
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/how-it-works")}>
                How LohaLink Works
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/insights")}>
                Market Insights
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/blog")}>
                Journal &amp; Blog
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => navigate("/about")}>
                About Us
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/careers")}>
                Careers
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Search */}
          <form
            onSubmit={onSearch}
            className="flex h-9 flex-1 overflow-hidden rounded-md ring-2 ring-transparent focus-within:ring-accent sm:h-10"
          >
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search HMS, billets, locations…"
              className="flex-1 bg-background px-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground sm:px-3"
              maxLength={80}
            />
            <button
              type="submit"
              className="flex flex-none items-center justify-center bg-accent px-3 text-accent-foreground hover:bg-accent/90 sm:px-4"
              aria-label="Search"
            >
              <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </form>

          {/* Right cluster */}
          <div className="flex flex-none items-center gap-1">

            {session && profile && <NotificationBell />}

            {session && profile ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="pop flex items-center gap-1 rounded-md p-1.5">
                    <User className="h-4 w-4" />
                    <div className="hidden leading-tight sm:block">
                      <div className="text-[10px] text-nav-foreground/70">
                        Hello, {profile.fullName?.split(" ")[0] || profile.email.split("@")[0]}
                      </div>
                      <div className="max-w-[160px] truncate text-sm font-bold">
                        {profile.businessName || profile.company || "Your account"}
                      </div>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>
                    <div className="font-semibold">
                      {profile.businessName || profile.company || "Your account"}
                    </div>
                    {profile.fullName && (
                      <div className="text-xs text-muted-foreground">
                        {profile.fullName}
                      </div>
                    )}
                    <div className="mt-1 truncate text-xs text-muted-foreground">
                      {profile.email}
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-wide text-accent">
                      {profile.role}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(portalPath)}>
                    {portalLabel}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <UserCog className="mr-2 h-4 w-4" /> Edit profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(isSeller ? "/seller/requirements" : "/buyer/requirements")
                    }
                  >
                    <ClipboardList className="mr-2 h-4 w-4 text-accent" />
                    {isSeller ? "Buyer requirements" : "My requirements"}
                  </DropdownMenuItem>
                  {!isSeller && (
                    <DropdownMenuItem onClick={() => navigate("/buyer/invoices")}>
                      Invoices
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin/overview")}>
                        <ShieldCheck className="mr-2 h-4 w-4 text-accent" /> Admin Console
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                size="sm"
                onClick={() => navigate("/auth")}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                Sign in
              </Button>
            )}
          </div>
        </div>

        {/* Live scrap rates ticker — deferred so it never blocks the marketplace */}
        <Suspense fallback={null}>
          <ScrapRatesTicker />
        </Suspense>
      </div>
    </header>
  );
};
